import React, { useMemo, Suspense, useSyncExternalStore } from 'react';
import { LayoutElement } from '../../types';
import { getOhtPivot, getOhtPivotWorld } from '../../utils/ohtPivot';
import { getFactory3DStyle, FACTORY_3D_PALETTE } from '../../utils/factory3dStyles';
import {
  getElement3DExtrusionDepth,
  getElement3DGroupY,
  TRACK_3D_MIN_THICKNESS,
  TRACK_3D_THICKNESS,
  isDxfEquipmentLayoutElement,
} from '../../utils/layout3d';
import { useRailElevation } from './railElevationContext';
import {
  ALL_RAIL_TYPES,
  isCurveRailType,
  isConnectorRailType,
  isJunctionRailType,
  getCircleJunctionMaxPorts,
  getCircleJunctionSpokesLocal,
  getCircleJunctionConnectedPortIndices,
  getCircleJunctionRingRadius,
  getCurveRadius,
  getCurveSweepAngle,
  getCurveSweepSign,
  getStraightRailCircleJunctionReach,
  resolveStraightRail3DSpan,
  RailShapeType,
} from '../../utils/topology';
import { getConnectorShiftState, getConnectorSolidLegLocalY } from '../../utils/connectorRail';
import { connectorShiftStore } from '../../store/connectorShiftStore';
import { junctionTurntableStore } from '../../store/junctionTurntableStore';
import { getTurntableSettledAngleDeg } from '../../utils/junctionTurntableSim';
import { AmhsStockerFromLayout3D, AmhsLoadPortFromLayout3D, AmhsEquipmentFromLayout3D } from './AmhsStockerFromLayout3D';
import { EquipmentFloorMesh } from './EquipmentMeshes3D';
import { OhtVehicle3DModel } from './OhtVehicle3DModel';
import {
  DualRailStraight3D,
  DualRailCurve3D,
  BlenderSwitch3D,
  CircleJunctionTurntableMesh3D,
} from './DualRailTrack3D';
import { useAppSelector } from '../../hooks/redux';

type Props = {
  element: LayoutElement;
  isConflict?: boolean;
  storedFoupsCount?: number;
};

function ConflictOutline({ w, h, d }: { w: number; h: number; d: number }) {
  return (
    <mesh position={[w / 2, d / 2, h / 2]} scale={[w + 10, d + 10, h + 10]}>
      <boxGeometry />
      <meshBasicMaterial color="#ef4444" wireframe />
    </mesh>
  );
}

/** Tall storage cabinet / stocker room (unchanged legacy fallback). */
const StockerMesh: React.FC<{ w: number; h: number; d: number; opacity: number }> = ({
  w,
  h,
  d,
  opacity,
}) => {
  const plinthH = d * 0.08;
  const shelfCount = 4;
  const shelves = useMemo(() => {
    const items: React.ReactNode[] = [];
    for (let i = 1; i <= shelfCount; i++) {
      const y = plinthH + (d - plinthH) * (i / (shelfCount + 1));
      items.push(
        <mesh key={i} position={[w / 2, y, h / 2]}>
          <boxGeometry args={[w * 0.92, d * 0.04, h * 0.88]} />
          <meshStandardMaterial
            color={FACTORY_3D_PALETTE.storage.shelf}
            metalness={0.92}
            roughness={0.34}
            transparent
            opacity={opacity}
          />
        </mesh>
      );
    }
    return items;
  }, [w, h, d, plinthH, opacity]);

  return (
    <>
      <mesh position={[w / 2, plinthH / 2, h / 2]}>
        <boxGeometry args={[w, plinthH, h]} />
        <meshStandardMaterial
          color={FACTORY_3D_PALETTE.storage.plinth}
          metalness={0.92}
          roughness={0.34}
          transparent
          opacity={opacity}
        />
      </mesh>
      <mesh position={[w / 2, plinthH + (d - plinthH) / 2, h / 2]}>
        <boxGeometry args={[w * 0.96, d - plinthH, h * 0.96]} />
        <meshStandardMaterial
          color={FACTORY_3D_PALETTE.storage.body}
          metalness={0.92}
          roughness={0.34}
          transparent
          opacity={opacity}
        />
      </mesh>
      <mesh position={[w * 0.08, plinthH + (d - plinthH) * 0.5, h / 2]}>
        <boxGeometry args={[w * 0.06, (d - plinthH) * 0.9, h * 0.04]} />
        <meshStandardMaterial
          color={FACTORY_3D_PALETTE.storage.accent}
          metalness={0.92}
          roughness={0.34}
        />
      </mesh>
      {shelves}
    </>
  );
};

/** Circle junction hub: recessed plate + rotating straight diameter dual-rail. */
const CircleJunctionTurntable3D: React.FC<{
  element: LayoutElement;
  layoutRails: LayoutElement[];
  cx: number;
  cy: number;
  ringR: number;
  junctionW: number;
  junctionH: number;
  d: number;
  beamW: number;
  opacity: number;
  spokeAngles?: number[];
}> = ({ element, layoutRails, cx, cy, ringR, junctionW, junctionH, d, beamW, opacity, spokeAngles }) => {
  const settled = getTurntableSettledAngleDeg(element, layoutRails);
  const hubAngleDeg = useSyncExternalStore(
    (cb) => junctionTurntableStore.subscribe(cb),
    () => Math.round(junctionTurntableStore.getHubAngle(element.id, settled) * 2) / 2,
    () => settled
  );
  const divert = Math.min(1, Math.abs(((hubAngleDeg - settled + 540) % 360) - 180) / 90);

  return (
    <group position={[cx, d / 2, cy]}>
      <CircleJunctionTurntableMesh3D
        ringR={ringR}
        junctionW={junctionW}
        junctionH={junctionH}
        depth={d}
        trackWidth={beamW}
        hubAngleDeg={hubAngleDeg}
        opacity={opacity}
        divert={divert}
        spokeAngles={spokeAngles}
      />
    </group>
  );
};

/** Dual-rail segment along an arbitrary XZ chord (layout local). */
const DualRailBeam: React.FC<{
  p1: [number, number, number];
  p2: [number, number, number];
  trackWidth: number;
  depth: number;
  opacity: number;
  glow?: 'done' | 'prog' | 'pend' | 'hold';
}> = ({ p1, p2, trackWidth, depth, opacity, glow = 'done' }) => {
  const [x1, y1, z1] = p1;
  const [x2, , z2] = p2;
  const dx = x2 - x1;
  const dz = z2 - z1;
  const len = Math.max(0.001, Math.hypot(dx, dz));
  const angle = -Math.atan2(dz, dx);
  return (
    <group position={[(x1 + x2) / 2, y1, (z1 + z2) / 2]} rotation={[0, angle, 0]}>
      <DualRailStraight3D
        length={len}
        trackWidth={trackWidth}
        depth={depth}
        opacity={opacity}
        glow={glow}
      />
    </group>
  );
};

/** Blender-style dual-rail + neon glow (create_track.py attachment look). */
const OverheadRailMesh: React.FC<{
  w: number;
  h: number;
  d: number;
  opacity: number;
  isCurve: boolean;
  railType?: string;
  maxPorts?: number;
  activePortIndices?: number[];
  element?: LayoutElement;
  elements?: Record<string, LayoutElement>;
}> = ({ w, h, d, opacity, isCurve, railType, maxPorts = 6, element, elements }) => {
  const trackW = TRACK_3D_THICKNESS;
  const layoutRails = useMemo(
    () => (elements ? Object.values(elements) : []),
    [elements]
  );
  const junctionReach = useMemo(() => {
    if (!element) return { extendStart: 0, extendEnd: 0 };
    return getStraightRailCircleJunctionReach(element, layoutRails, trackW);
  }, [element, layoutRails, trackW]);
  const straightSpan = useMemo(() => {
    if (!element) {
      const alongX = w >= h;
      return {
        span: alongX ? w : h,
        centerLocalX: w / 2,
        centerLocalZ: h / 2,
        alongX,
      };
    }
    return resolveStraightRail3DSpan(element, layoutRails, trackW);
  }, [element, layoutRails, trackW, w, h]);

  const radius = getCurveRadius(w, h);
  const sweepSign = element ? getCurveSweepSign(element) : 1;
  const arcAngle =
    (sweepSign * getCurveSweepAngle((railType || 'curve90') as RailShapeType) * Math.PI) / 180;

  if (isCurve) {
    return (
      <group position={[0, d / 2, 0]}>
        <DualRailCurve3D
          radius={radius}
          sweepRad={arcAngle}
          trackWidth={Math.max(trackW, TRACK_3D_MIN_THICKNESS)}
          depth={d}
          opacity={opacity}
          glow="done"
          steps={12}
          junctionReachStart={junctionReach.extendStart}
          junctionReachEnd={junctionReach.extendEnd}
        />
      </group>
    );
  }

  if (isJunctionRailType(railType as RailShapeType)) {
    const beamW = Math.max(TRACK_3D_MIN_THICKNESS, h >= 20 ? TRACK_3D_THICKNESS : h);
    const junctionMeshes: React.ReactNode[] = [];
    if (railType === 'junction-circle') {
      const cx = w / 2;
      const cy = h / 2;
      const ringR = getCircleJunctionRingRadius(w, h);
      const spokes = getCircleJunctionSpokesLocal(w, h, maxPorts);
      if (element) {
        const spokeAngles = spokes.map((s) => s.angle);
        junctionMeshes.push(
          <CircleJunctionTurntable3D
            key="turntable"
            element={element}
            layoutRails={elements ? Object.values(elements) : []}
            cx={cx}
            cy={cy}
            ringR={ringR}
            junctionW={w}
            junctionH={h}
            d={d}
            beamW={beamW}
            opacity={opacity}
            spokeAngles={spokeAngles}
          />
        );
      }
    }
    return <>{junctionMeshes}</>;
  }

  const { span, centerLocalX, centerLocalZ, alongX } = straightSpan;
  return (
    <group
      position={[centerLocalX, d / 2, centerLocalZ]}
      rotation={alongX ? [0, 0, 0] : [0, Math.PI / 2, 0]}
    >
      <DualRailStraight3D
        length={span}
        trackWidth={trackW}
        depth={d}
        opacity={opacity}
        glow="done"
      />
    </group>
  );
};

/** Floor lane / zone annotation. */
const LaneZoneMesh: React.FC<{
  w: number;
  h: number;
  d: number;
  color: string;
  opacity: number;
}> = ({ w, h, d, color, opacity }) => (
  <mesh position={[w / 2, d / 2, h / 2]}>
    <boxGeometry args={[w, Math.max(d, 2), h]} />
    <meshStandardMaterial
      color={color}
      transparent
      opacity={opacity}
      metalness={0.05}
      roughness={0.9}
    />
  </mesh>
);


/** Horizontal beam connecting pillars. */
const HorizontalBeamMesh: React.FC<{ w: number; h: number; d: number; opacity: number }> = ({
  w,
  h,
  d,
  opacity,
}) => {
  return (
    <mesh position={[w / 2, d / 2, h / 2]} castShadow receiveShadow>
      <boxGeometry args={[w, d, h]} />
      <meshStandardMaterial
        color="#B8BDC1"
        metalness={0.8}
        roughness={0.32}
        transparent={opacity < 1}
        opacity={opacity}
      />
    </mesh>
  );
};

/** Suspension rod hanging from beam to track. */
const SuspensionRodMesh: React.FC<{ w: number; h: number; d: number; opacity: number }> = ({
  w,
  h,
  d,
  opacity,
}) => {
  const radius = Math.min(w, h) / 2;
  return (
    <>
      {/* Main rod */}
      <mesh position={[w / 2, -d / 2, h / 2]} castShadow receiveShadow>
        <cylinderGeometry args={[radius, radius, d, 8]} />
        <meshStandardMaterial
          color="#A7B0B8"
          metalness={0.74}
          roughness={0.38}
          transparent={opacity < 1}
          opacity={opacity}
        />
      </mesh>
      {/* Top connection point */}
      <mesh position={[w / 2, 0, h / 2]} castShadow receiveShadow>
        <sphereGeometry args={[radius * 1.5, 8, 8]} />
        <meshStandardMaterial
          color="#8892A0"
          metalness={0.7}
          roughness={0.3}
          transparent={opacity < 1}
          opacity={opacity}
        />
      </mesh>
      {/* Bottom connection point */}
      <mesh position={[w / 2, -d, h / 2]} castShadow receiveShadow>
        <sphereGeometry args={[radius * 1.5, 8, 8]} />
        <meshStandardMaterial
          color="#8892A0"
          metalness={0.7}
          roughness={0.3}
          transparent={opacity < 1}
          opacity={opacity}
        />
      </mesh>
    </>
  );
};

/**
 * H-connector: dual-rail legs + crossbar + movable segment with Blender switch throw.
 */
const ConnectorRailMesh: React.FC<{
  element: LayoutElement;
  w: number;
  h: number;
  d: number;
  opacity: number;
}> = ({ element, w, h, d, opacity }) => {
  const solidProgress = useSyncExternalStore(
    (onStoreChange) => connectorShiftStore.subscribe(onStoreChange),
    () => connectorShiftStore.getSolidProgress(element.id, getConnectorShiftState(element)),
    () => (getConnectorShiftState(element) === 'shifted' ? 1 : 0)
  );

  const strokeW = Math.max(TRACK_3D_MIN_THICKNESS, Math.min(TRACK_3D_THICKNESS, Math.min(w, h) * 0.25));
  const solidZ = getConnectorSolidLegLocalY(element, solidProgress);
  const guideOpacity = opacity * 0.45;
  const y = d / 2;

  return (
    <group>
      <DualRailBeam
        p1={[0, y, 0]}
        p2={[w, y, 0]}
        trackWidth={strokeW}
        depth={d}
        opacity={guideOpacity}
        glow="pend"
      />
      <DualRailBeam
        p1={[0, y, h]}
        p2={[w, y, h]}
        trackWidth={strokeW}
        depth={d}
        opacity={guideOpacity}
        glow="pend"
      />
      <DualRailBeam
        p1={[w / 2, y, 0]}
        p2={[w / 2, y, h]}
        trackWidth={strokeW * 0.85}
        depth={d * 0.85}
        opacity={guideOpacity}
        glow="prog"
      />
      <DualRailBeam
        p1={[0, y, solidZ]}
        p2={[w, y, solidZ]}
        trackWidth={strokeW * 1.45}
        depth={d * 1.15}
        opacity={opacity}
        glow="hold"
      />
      <group position={[w / 2, y + d * 0.55, solidZ]}>
        <BlenderSwitch3D size={strokeW * 2.2} depth={d * 0.75} divert={solidProgress} />
      </group>
    </group>
  );
};

const FactoryElement3D: React.FC<Props> = React.memo(({ element, isConflict, storedFoupsCount }) => {
  const { position, rotation, dimensions, metadata } = element;
  const elements = useAppSelector((s) => s.layout.elements);
  const w = dimensions.width;
  const h = dimensions.height;
  const d = getElement3DExtrusionDepth(element);

  const x = position.x;
  const z = position.y;
  const y = getElement3DGroupY(element, undefined, useRailElevation());
  const opacity = metadata.opacity ?? 1;
  const railType =
    typeof element.customProperties?.railType === 'string'
      ? element.customProperties.railType
      : undefined;
  const isCurve =
    ALL_RAIL_TYPES.has(element.type) &&
    isCurveRailType(railType as RailShapeType);
  const style = getFactory3DStyle(element.type);

  const content = (() => {
    switch (element.type) {
      case 'stocker':
        return (
          <Suspense fallback={<StockerMesh w={w} h={h} d={d} opacity={opacity} />}>
            <AmhsStockerFromLayout3D
              w={w}
              h={h}
              opacity={opacity}
              bayCount={element.customProperties?.bayCount as string | number}
              outsideStocker={element.customProperties?.outsideStocker === true}
              storedFoupsCount={storedFoupsCount}
              elementId={element.id}
              label={element.metadata?.name || 'Stocker'}
            />
          </Suspense>
        );
      case 'oht-vehicle': {
        const isLoaded =
          element.customProperties?.loaded === true ||
          element.customProperties?.loaded === 'true';
        const { pivotX, pivotY } = getOhtPivot(element, w, h);
        return (
          <Suspense
            fallback={
              <mesh position={[w / 2, d / 2, h / 2]}>
                <boxGeometry args={[w, d, h]} />
                <meshStandardMaterial
                  color={FACTORY_3D_PALETTE.oht.body}
                  transparent
                  opacity={opacity}
                />
              </mesh>
            }
          >
            {/* Local origin is rail pivot — parent group is placed at pivot world. */}
            <group position={[-pivotX, 0, -pivotY]}>
              <OhtVehicle3DModel
                w={w}
                h={h}
                depth={d}
                opacity={opacity}
                paused
                groupY={y}
                isLoaded={isLoaded}
                pivotX={pivotX}
                pivotY={pivotY}
              />
            </group>
          </Suspense>
        );
      }
      case 'overhead-rail':
      case 'overhead-curve':
      case 'cross-rail': {
        if (isConnectorRailType(railType as RailShapeType)) {
          return (
            <ConnectorRailMesh element={element} w={w} h={h} d={d} opacity={opacity} />
          );
        }
        const maxPorts = getCircleJunctionMaxPorts(element);
        const railMesh = isCurve ? (
          <OverheadRailMesh
            w={w}
            h={h}
            d={d}
            opacity={opacity}
            isCurve={isCurve}
            railType={railType}
            element={element}
          />
        ) : (
          <OverheadRailMesh
            w={w}
            h={h}
            d={d}
            opacity={opacity}
            isCurve={false}
            railType={railType}
            maxPorts={maxPorts}
            activePortIndices={[...getCircleJunctionConnectedPortIndices(element, Object.values(elements))]}
            element={element}
            elements={elements}
          />
        );
        return (
          <Suspense
            fallback={
              <mesh position={[w / 2, d / 2, h / 2]}>
                <boxGeometry args={[w, d, h]} />
                <meshStandardMaterial
                  color={FACTORY_3D_PALETTE.rail.beam}
                  transparent
                  opacity={opacity}
                />
              </mesh>
            }
          >
            {railMesh}
          </Suspense>
        );
      }
      case 'stb':
        return (
          <Suspense fallback={<EquipmentFloorMesh type="stb" w={w} h={h} d={d} opacity={opacity} />}>
            <AmhsLoadPortFromLayout3D
              w={w}
              h={h}
              depth={d}
              opacity={opacity}
              label={element.metadata?.name || 'STB'}
              element={element}
            />
          </Suspense>
        );
      case 'ohb':
        return (
          <AmhsEquipmentFromLayout3D
            w={w}
            h={h}
            depth={d}
            opacity={opacity}
            element={element}
            type="AMHS_INTERFACE"
            loadPortCount={2}
            label={element.metadata?.name || 'OHB'}
          />
        );
      case 'lifter':
        return (
          <AmhsEquipmentFromLayout3D
            w={w}
            h={h}
            depth={d}
            opacity={opacity}
            element={element}
            type="AMHS_INTERFACE"
            loadPortCount={2}
            label={element.metadata?.name || 'LIFTER'}
          />
        );
      case 'support-pillar':
        return null;
      case 'horizontal-beam':
        return <HorizontalBeamMesh w={w} h={h} d={d} opacity={opacity} />;
      case 'suspension-rod':
        return <SuspensionRodMesh w={w} h={h} d={d} opacity={opacity} />;
      case 'rectangle':
        if (isDxfEquipmentLayoutElement(element)) {
          return (
            <Suspense
              fallback={
                <mesh position={[w / 2, d / 2, h / 2]} castShadow receiveShadow>
                  <boxGeometry args={[w, d, h]} />
                  <meshStandardMaterial
                    color={FACTORY_3D_PALETTE.fabEquipment.body}
                    metalness={0.85}
                    roughness={0.28}
                    transparent={opacity < 1}
                    opacity={opacity}
                  />
                </mesh>
              }
            >
              <AmhsEquipmentFromLayout3D
                w={w}
                h={h}
                depth={d}
                opacity={opacity}
                element={element}
                label={element.metadata?.name}
              />
            </Suspense>
          );
        }
        return (
          <LaneZoneMesh
            w={w}
            h={h}
            d={d}
            color={FACTORY_3D_PALETTE.floor.laneFill}
            opacity={style.opacity ?? 0.25}
          />
        );
      case 'circle':
        return (
          <mesh position={[w / 2, d / 2, h / 2]}>
            <cylinderGeometry args={[Math.min(w, h) / 2, Math.min(w, h) / 2, d, 24]} />
            <meshStandardMaterial color={style.color} transparent opacity={style.opacity ?? 0.4} />
          </mesh>
        );
      default:
        return (
          <mesh position={[w / 2, d / 2, h / 2]} scale={[w, d, h]}>
            <boxGeometry />
            <meshStandardMaterial color={style.color} transparent opacity={opacity} />
          </mesh>
        );
    }
  })();

  // OHT rotates about the rail hang point (vehicle centre), not the bbox corner.
  const isOht = element.type === 'oht-vehicle';
  const ohtPivot = isOht ? getOhtPivot(element, w, h) : null;
  const ohtWorld = isOht ? getOhtPivotWorld(element, w, h) : null;
  const groupX = ohtWorld ? ohtWorld.x : x;
  const groupZ = ohtWorld ? ohtWorld.y : z;

  return (
    <group position={[groupX, y, groupZ]} rotation={[0, (-rotation * Math.PI) / 180, 0]}>
      {content}
      {isConflict && !isCurve && !ohtPivot && <ConflictOutline w={w} h={h} d={d} />}
      {isConflict && !isCurve && ohtPivot && (
        <group position={[-ohtPivot.pivotX, 0, -ohtPivot.pivotY]}>
          <ConflictOutline w={w} h={h} d={d} />
        </group>
      )}
    </group>
  );
});

FactoryElement3D.displayName = 'FactoryElement3D';

export default FactoryElement3D;
